/**
 * Swordsman Renderer - Specialized renderer for swordsman units with melee attack animation
 * Features:
 * - Procedural sword swing attack animation via custom shaders
 * - Wind-up, swing, follow-through and recovery phases
 * - Body momentum and footwork during attack
 * - Walk animation with sword-ready stance
 */

import * as THREE from 'three';
import type { FactionId, Entity } from '@/types';
import { EventBus } from '@/core/events/EventBus';
import { COLORS, FACTION_COLORS } from './unitConstants';

const MAX_SWORDSMEN = 800;

// Animation group IDs encoded in vertex attribute
export const SWORDSMAN_ANIM_GROUP = {
  STATIC: 0,       // No animation (decorative elements)
  BODY: 1,         // Torso - rotates during swing
  HEAD: 2,         // Head - follows body rotation
  LEFT_ARM: 3,     // Left arm (balance/follow through)
  RIGHT_ARM: 4,    // Right arm (sword arm - main attack motion)
  LEFT_LEG: 5,     // Left leg - plants during swing
  RIGHT_LEG: 6,    // Right leg - steps forward
  SWORD: 7,        // Sword - follows right arm with added rotation
  LEFT_SHOULDER: 8,  // Left shoulder armor
  RIGHT_SHOULDER: 9, // Right shoulder armor
};

interface SwordsmanData {
  entity: Entity;
  x: number;
  y: number;
  z: number;
  factionId: FactionId;
  rotation: number;
  speed: number;           // Movement speed for walk animation
  animPhase: number;       // Animation phase offset
  attackTime: number;      // Time when attack started (-1 = not attacking)
  attackDuration: number;  // Duration of attack animation
}

// Vertex shader with sword attack animation
const swordsmanVertexShader = `
  attribute vec3 color;
  attribute float animGroup;

  uniform float uTime;

  // Per-instance attributes
  attribute float instanceSpeed;
  attribute float instanceAnimPhase;
  attribute float instanceAttackTime;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  // Animation parameters
  const float PI = 3.14159265359;
  const float WALK_FREQUENCY = 4.5;
  const float ATTACK_DURATION = 0.55;  // Attack animation duration in seconds

  // Rotation helper - rotate around Y axis
  vec3 rotateY(vec3 pos, float angle, vec3 pivot) {
    vec3 p = pos - pivot;
    float c = cos(angle);
    float s = sin(angle);
    return vec3(
      p.x * c + p.z * s,
      p.y,
      -p.x * s + p.z * c
    ) + pivot;
  }

  // Rotation helper - rotate around X axis
  vec3 rotateX(vec3 pos, float angle, vec3 pivot) {
    vec3 p = pos - pivot;
    float c = cos(angle);
    float s = sin(angle);
    return vec3(
      p.x,
      p.y * c - p.z * s,
      p.y * s + p.z * c
    ) + pivot;
  }

  vec3 animateVertex(vec3 pos, float group, float time, float speed, float phase, float attackTime) {
    vec3 animated = pos;

    // Calculate attack progress (0 = not attacking, 0-1 = attack progress)
    float attackProgress = 0.0;
    float attackPhase = 0.0;
    if (attackTime >= 0.0) {
      float timeSinceAttack = time - attackTime;
      if (timeSinceAttack < ATTACK_DURATION) {
        attackProgress = timeSinceAttack / ATTACK_DURATION;
        // Attack phases:
        // 0-0.25 = wind up (raise sword)
        // 0.25-0.45 = swing down
        // 0.45-0.6 = follow through
        // 0.6-1.0 = recover
        if (attackProgress < 0.25) {
          attackPhase = attackProgress / 0.25;  // 0-1 wind up
        } else if (attackProgress < 0.45) {
          attackPhase = 1.0 + (attackProgress - 0.25) / 0.2;  // 1-2 swing
        } else if (attackProgress < 0.6) {
          attackPhase = 2.0 + (attackProgress - 0.45) / 0.15;  // 2-3 follow through
        } else {
          attackPhase = 3.0 + (attackProgress - 0.6) / 0.4;  // 3-4 recover
        }
      }
    }

    // Walking animation
    float walkIntensity = clamp(speed, 0.0, 1.0);
    float t = time * WALK_FREQUENCY + phase;

    // Idle sway
    float idleSway = sin(time * 1.5 + phase) * 0.003 * (1.0 - walkIntensity);
    float idleBreath = sin(time * 1.0 + phase) * 0.002 * (1.0 - walkIntensity);

    // Body pivot point (center of torso)
    vec3 bodyPivot = vec3(0.0, 0.36, 0.0);
    // Shoulder pivot point (for arms)
    vec3 rightShoulderPivot = vec3(0.09, 0.42, 0.0);
    vec3 leftShoulderPivot = vec3(-0.09, 0.42, 0.0);

    if (group < 0.5) {
      // STATIC - no animation
      return animated;
    }

    // Apply idle sway to all animated parts
    animated.z += idleSway;

    if (group < 1.5) {
      // BODY - rotates during swing
      float walkBob = sin(t * 2.0) * 0.003 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float rotationAmount = 0.0;
        float leanForward = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - rotate body back, lean back slightly
          rotationAmount = -0.15 * attackPhase;
          leanForward = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Swing - rotate body forward powerfully
          float swingPhase = attackPhase - 1.0;
          rotationAmount = -0.15 + 0.4 * swingPhase;
          leanForward = -0.008 + 0.025 * swingPhase;
        } else if (attackPhase < 3.0) {
          // Follow through - continue rotation
          float followPhase = attackPhase - 2.0;
          rotationAmount = 0.25 + 0.1 * followPhase;
          leanForward = 0.017 + 0.005 * followPhase;
        } else {
          // Recover - return to neutral
          float recoverPhase = attackPhase - 3.0;
          rotationAmount = 0.35 * (1.0 - recoverPhase);
          leanForward = 0.022 * (1.0 - recoverPhase);
        }

        animated = rotateY(animated, rotationAmount, bodyPivot);
        animated.z += leanForward;
      }
    }
    else if (group < 2.5) {
      // HEAD - follows body, looks at target during swing
      float walkBob = sin(t * 2.0 + 0.1) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float rotationAmount = 0.0;
        float focusForward = 0.0;

        if (attackPhase < 1.0) {
          rotationAmount = -0.12 * attackPhase;
        } else if (attackPhase < 2.0) {
          float swingPhase = attackPhase - 1.0;
          rotationAmount = -0.12 + 0.35 * swingPhase;
          focusForward = 0.005 * swingPhase;
        } else if (attackPhase < 3.0) {
          float followPhase = attackPhase - 2.0;
          rotationAmount = 0.23 + 0.07 * followPhase;
          focusForward = 0.005 - 0.003 * followPhase;
        } else {
          float recoverPhase = attackPhase - 3.0;
          rotationAmount = 0.3 * (1.0 - recoverPhase);
          focusForward = 0.002 * (1.0 - recoverPhase);
        }

        animated = rotateY(animated, rotationAmount, bodyPivot);
        animated.z += focusForward;
      }
    }
    else if (group < 3.5) {
      // LEFT_ARM - balance arm, swings opposite to attack
      float walkSwing = sin(t) * 0.008 * walkIntensity;
      animated.z += walkSwing * 0.5;

      if (attackPhase > 0.0) {
        float swingBack = 0.0;
        float raiseAmount = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - left arm goes forward for balance
          swingBack = 0.015 * attackPhase;
          raiseAmount = 0.005 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Swing - left arm swings back
          float swingPhase = attackPhase - 1.0;
          swingBack = 0.015 - 0.035 * swingPhase;
          raiseAmount = 0.005 + 0.008 * swingPhase;
        } else if (attackPhase < 3.0) {
          // Follow through
          float followPhase = attackPhase - 2.0;
          swingBack = -0.02 - 0.01 * followPhase;
          raiseAmount = 0.013 - 0.005 * followPhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          swingBack = -0.03 * (1.0 - recoverPhase);
          raiseAmount = 0.008 * (1.0 - recoverPhase);
        }

        animated.z += swingBack;
        animated.y += raiseAmount;
        animated = rotateY(animated, swingBack * 2.0, leftShoulderPivot);
      }
    }
    else if (group < 4.5) {
      // RIGHT_ARM (sword arm) - main attack motion
      float walkSwing = -sin(t) * 0.006 * walkIntensity;
      animated.z += walkSwing * 0.3;

      if (attackPhase > 0.0) {
        float swingForward = 0.0;
        float raiseAmount = 0.0;
        float rotateOut = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - raise arm up and back
          swingForward = -0.04 * attackPhase;
          raiseAmount = 0.06 * attackPhase;
          rotateOut = -0.02 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Swing - arm comes down and forward fast
          float swingPhase = attackPhase - 1.0;
          float easeOut = 1.0 - (1.0 - swingPhase) * (1.0 - swingPhase);
          swingForward = -0.04 + 0.12 * easeOut;
          raiseAmount = 0.06 - 0.08 * easeOut;
          rotateOut = -0.02 + 0.04 * easeOut;
        } else if (attackPhase < 3.0) {
          // Follow through - continue forward motion
          float followPhase = attackPhase - 2.0;
          swingForward = 0.08 + 0.02 * followPhase;
          raiseAmount = -0.02 - 0.02 * followPhase;
          rotateOut = 0.02 + 0.01 * followPhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          swingForward = 0.1 * (1.0 - recoverPhase);
          raiseAmount = -0.04 * (1.0 - recoverPhase);
          rotateOut = 0.03 * (1.0 - recoverPhase);
        }

        animated.z += swingForward;
        animated.y += raiseAmount;
        animated.x += rotateOut;
      }
    }
    else if (group < 5.5) {
      // LEFT_LEG - plants during swing
      float cycle = t;
      float lift = max(sin(cycle), 0.0) * 0.008 * walkIntensity;
      float swing = sin(cycle) * 0.006 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      if (attackPhase > 0.0) {
        float plantBack = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - left leg steps back for stability
          plantBack = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Swing - leg stays planted
          plantBack = -0.008;
        } else if (attackPhase < 3.0) {
          // Follow through - push off
          float followPhase = attackPhase - 2.0;
          plantBack = -0.008 + 0.005 * followPhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          plantBack = -0.003 * (1.0 - recoverPhase);
        }
        animated.z += plantBack;
      }
    }
    else if (group < 6.5) {
      // RIGHT_LEG - steps forward during swing
      float cycle = t + PI;
      float lift = max(sin(cycle), 0.0) * 0.008 * walkIntensity;
      float swing = sin(cycle) * 0.006 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      if (attackPhase > 0.0) {
        float stepForward = 0.0;
        float liftUp = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - slight lift
          liftUp = 0.003 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Swing - step forward with the attack
          float swingPhase = attackPhase - 1.0;
          stepForward = 0.015 * swingPhase;
          liftUp = 0.003 - 0.003 * swingPhase;
        } else if (attackPhase < 3.0) {
          // Follow through - land and plant
          float followPhase = attackPhase - 2.0;
          stepForward = 0.015 + 0.005 * followPhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          stepForward = 0.02 * (1.0 - recoverPhase);
        }

        animated.z += stepForward;
        animated.y += liftUp;
      }
    }
    else if (group < 7.5) {
      // SWORD - follows arm but with exaggerated rotation for slash effect
      float idleSwordSway = sin(time * 0.8 + phase) * 0.003 * (1.0 - walkIntensity);
      animated.x += idleSwordSway;

      if (attackPhase > 0.0) {
        float swingForward = 0.0;
        float raiseAmount = 0.0;
        float swordRotate = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - sword goes up and back with extra arc
          swingForward = -0.06 * attackPhase;
          raiseAmount = 0.10 * attackPhase;
          swordRotate = -0.04 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Swing - sword arcs down fast with momentum
          float swingPhase = attackPhase - 1.0;
          float easeOut = 1.0 - (1.0 - swingPhase) * (1.0 - swingPhase);
          swingForward = -0.06 + 0.20 * easeOut;
          raiseAmount = 0.10 - 0.18 * easeOut;
          swordRotate = -0.04 + 0.10 * easeOut;
        } else if (attackPhase < 3.0) {
          // Follow through - sword continues arc
          float followPhase = attackPhase - 2.0;
          swingForward = 0.14 + 0.04 * followPhase;
          raiseAmount = -0.08 - 0.03 * followPhase;
          swordRotate = 0.06 + 0.02 * followPhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          swingForward = 0.18 * (1.0 - recoverPhase);
          raiseAmount = -0.11 * (1.0 - recoverPhase);
          swordRotate = 0.08 * (1.0 - recoverPhase);
        }

        animated.z += swingForward;
        animated.y += raiseAmount;
        animated.x += swordRotate;

        // Add sword rotation around its axis during swing
        vec3 swordPivot = vec3(0.125, 0.24, 0.0);
        if (attackPhase >= 1.0 && attackPhase < 3.0) {
          float rotAngle = (attackPhase - 1.0) * 0.3;
          animated = rotateX(animated, rotAngle, swordPivot);
        }
      }
    }
    else if (group < 8.5) {
      // LEFT_SHOULDER - follows body with slight delay
      float walkBob = sin(t * 2.0 + 0.15) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float rotationAmount = 0.0;

        if (attackPhase < 1.0) {
          rotationAmount = -0.12 * attackPhase;
        } else if (attackPhase < 2.0) {
          float swingPhase = attackPhase - 1.0;
          rotationAmount = -0.12 + 0.32 * swingPhase;
        } else if (attackPhase < 3.0) {
          float followPhase = attackPhase - 2.0;
          rotationAmount = 0.2 + 0.08 * followPhase;
        } else {
          float recoverPhase = attackPhase - 3.0;
          rotationAmount = 0.28 * (1.0 - recoverPhase);
        }

        animated = rotateY(animated, rotationAmount, bodyPivot);
      }
    }
    else if (group < 9.5) {
      // RIGHT_SHOULDER - follows body with arm motion
      float walkBob = sin(t * 2.0 - 0.1) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float rotationAmount = 0.0;
        float raiseAmount = 0.0;

        if (attackPhase < 1.0) {
          rotationAmount = -0.1 * attackPhase;
          raiseAmount = 0.02 * attackPhase;
        } else if (attackPhase < 2.0) {
          float swingPhase = attackPhase - 1.0;
          rotationAmount = -0.1 + 0.3 * swingPhase;
          raiseAmount = 0.02 - 0.03 * swingPhase;
        } else if (attackPhase < 3.0) {
          float followPhase = attackPhase - 2.0;
          rotationAmount = 0.2 + 0.06 * followPhase;
          raiseAmount = -0.01 - 0.01 * followPhase;
        } else {
          float recoverPhase = attackPhase - 3.0;
          rotationAmount = 0.26 * (1.0 - recoverPhase);
          raiseAmount = -0.02 * (1.0 - recoverPhase);
        }

        animated = rotateY(animated, rotationAmount, bodyPivot);
        animated.y += raiseAmount;
      }
    }

    return animated;
  }

  void main() {
    vColor = color;
    vNormal = normalize(normalMatrix * normal);

    // Get instance-specific animation parameters
    float speed = instanceSpeed;
    float phase = instanceAnimPhase;
    float attackTime = instanceAttackTime;

    // Animate vertex
    vec3 animatedPosition = animateVertex(position, animGroup, uTime, speed, phase, attackTime);

    vec4 worldPosition = instanceMatrix * vec4(animatedPosition, 1.0);
    vWorldPosition = worldPosition.xyz;

    vec4 mvPosition = modelViewMatrix * worldPosition;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Fragment shader with faction colors and lighting
const swordsmanFragmentShader = `
  uniform vec3 primaryColor;
  uniform vec3 secondaryColor;
  uniform vec3 ambientLightColor;
  uniform vec3 directionalLightColor;
  uniform vec3 directionalLightDirection;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    vec3 finalColor = vColor;

    // Faction color replacement (red = primary, green = secondary)
    if (vColor.r > 0.9 && vColor.g < 0.2 && vColor.b < 0.2) {
      finalColor = primaryColor;
    } else if (vColor.g > 0.9 && vColor.r < 0.2 && vColor.b < 0.2) {
      finalColor = secondaryColor;
    }

    // Lighting
    vec3 normal = normalize(vNormal);
    vec3 lightDir = normalize(directionalLightDirection);

    float NdotL = dot(normal, lightDir);
    float diff = max(NdotL, 0.0);
    float wrap = max(NdotL * 0.5 + 0.5, 0.0);

    // Rim lighting for heroic effect
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float rim = 1.0 - max(dot(viewDir, normal), 0.0);
    rim = pow(rim, 4.0) * 0.18;

    // Combine lighting
    vec3 ambient = ambientLightColor * 0.5;
    vec3 diffuse = directionalLightColor * diff * 0.38;
    vec3 fill = directionalLightColor * wrap * 0.15;
    vec3 rimColor = vec3(1.0, 0.95, 0.9) * rim;

    vec3 litColor = finalColor * (ambient + diffuse + fill) + rimColor;

    // Slight color variation
    litColor *= 0.95 + 0.1 * fract(sin(dot(vWorldPosition.xz, vec2(12.9898, 78.233))) * 43758.5453);

    gl_FragColor = vec4(litColor, 1.0);
  }
`;

export class SwordsmanRenderer {
  private scene: THREE.Scene;
  private instancedMeshes: Map<FactionId, THREE.InstancedMesh> = new Map();
  private swordsmanData: SwordsmanData[] = [];
  private entityMap: Map<Entity, SwordsmanData> = new Map();
  private materials: Map<FactionId, THREE.ShaderMaterial> = new Map();
  private dummy = new THREE.Object3D();
  private time = 0;

  // Instance attributes for animation
  private speedAttribute: THREE.InstancedBufferAttribute | null = null;
  private phaseAttribute: THREE.InstancedBufferAttribute | null = null;
  private attackTimeAttribute: THREE.InstancedBufferAttribute | null = null;

  // Event listener cleanup
  private attackEventHandler: ((data: { attackerId: Entity }) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createGeometryAndMeshes();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for attack events to trigger attack animation
    this.attackEventHandler = (data: { attackerId: Entity }) => {
      const swordsman = this.entityMap.get(data.attackerId);
      if (swordsman) {
        swordsman.attackTime = this.time;
        swordsman.attackDuration = 0.55;
      }
    };
    EventBus.on('unit:attack', this.attackEventHandler);
  }

  private createGeometryAndMeshes(): void {
    const geometry = this.createSwordsmanGeometry();

    // Create instance attributes for animation
    const speeds = new Float32Array(MAX_SWORDSMEN);
    const phases = new Float32Array(MAX_SWORDSMEN);
    const attackTimes = new Float32Array(MAX_SWORDSMEN);
    for (let i = 0; i < MAX_SWORDSMEN; i++) {
      speeds[i] = 0;
      phases[i] = Math.random() * Math.PI * 2;
      attackTimes[i] = -1;  // Not attacking
    }

    this.speedAttribute = new THREE.InstancedBufferAttribute(speeds, 1);
    this.phaseAttribute = new THREE.InstancedBufferAttribute(phases, 1);
    this.attackTimeAttribute = new THREE.InstancedBufferAttribute(attackTimes, 1);

    geometry.setAttribute('instanceSpeed', this.speedAttribute);
    geometry.setAttribute('instanceAnimPhase', this.phaseAttribute);
    geometry.setAttribute('instanceAttackTime', this.attackTimeAttribute);

    // Create mesh for each faction
    for (const [factionId, colors] of Object.entries(FACTION_COLORS)) {
      const material = new THREE.ShaderMaterial({
        vertexShader: swordsmanVertexShader,
        fragmentShader: swordsmanFragmentShader,
        uniforms: {
          primaryColor: { value: colors.primary },
          secondaryColor: { value: colors.secondary },
          ambientLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightDirection: { value: new THREE.Vector3(0.5, 1.0, 0.3).normalize() },
          uTime: { value: 0 },
        },
      });
      this.materials.set(factionId as FactionId, material);

      const mesh = new THREE.InstancedMesh(geometry, material, MAX_SWORDSMEN);
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      this.instancedMeshes.set(factionId as FactionId, mesh);
      this.scene.add(mesh);
    }
  }

  private createSwordsmanGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const colors: THREE.Color[] = [];
    const animGroups: number[] = [];

    // ==================== HEAD ====================
    const head = new THREE.SphereGeometry(0.08, 10, 8);
    head.translate(0, 0.52, 0);
    parts.push(head);
    colors.push(COLORS.skin);
    animGroups.push(SWORDSMAN_ANIM_GROUP.HEAD);

    // ==================== HELMET ====================
    // Helmet dome
    const helmetDome = new THREE.SphereGeometry(
      0.085, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.55
    );
    helmetDome.translate(0, 0.54, 0);
    parts.push(helmetDome);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.HEAD);

    // Helmet rim
    const helmetRim = new THREE.TorusGeometry(0.082, 0.01, 4, 12);
    helmetRim.rotateX(Math.PI / 2);
    helmetRim.translate(0, 0.54, 0);
    parts.push(helmetRim);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.HEAD);

    // Forehead plate
    const foreheadPlate = new THREE.BoxGeometry(0.06, 0.02, 0.012);
    foreheadPlate.translate(0, 0.52, 0.07);
    parts.push(foreheadPlate);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.HEAD);

    // Helmet spike
    const spike = new THREE.ConeGeometry(0.015, 0.05, 6);
    spike.translate(0, 0.66, 0);
    parts.push(spike);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.HEAD);

    // Spike base
    const spikeBase = new THREE.CylinderGeometry(0.018, 0.015, 0.012, 6);
    spikeBase.translate(0, 0.63, 0);
    parts.push(spikeBase);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.HEAD);

    // ==================== TORSO ====================
    const torso = new THREE.CylinderGeometry(0.07, 0.08, 0.16, 8);
    torso.translate(0, 0.36, 0);
    parts.push(torso);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.BODY);

    // Chest plate decoration
    const chestPlate = new THREE.CylinderGeometry(0.03, 0.03, 0.012, 8);
    chestPlate.rotateX(Math.PI / 2);
    chestPlate.translate(0, 0.38, 0.072);
    parts.push(chestPlate);
    colors.push(COLORS.bronze);
    animGroups.push(SWORDSMAN_ANIM_GROUP.BODY);

    // Belt
    const belt = new THREE.CylinderGeometry(0.082, 0.082, 0.02, 8);
    belt.translate(0, 0.27, 0);
    parts.push(belt);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.BODY);

    // Belt buckle
    const buckle = new THREE.BoxGeometry(0.025, 0.02, 0.012);
    buckle.translate(0, 0.27, 0.08);
    parts.push(buckle);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.BODY);

    // Skirt armor
    const skirt = new THREE.CylinderGeometry(0.08, 0.10, 0.08, 8);
    skirt.translate(0, 0.20, 0);
    parts.push(skirt);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.BODY);

    // ==================== SHOULDER ARMOR ====================
    // Left shoulder
    const leftShoulder = new THREE.BoxGeometry(0.045, 0.018, 0.05);
    leftShoulder.translate(-0.09, 0.42, 0);
    parts.push(leftShoulder);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.LEFT_SHOULDER);

    // Right shoulder
    const rightShoulder = new THREE.BoxGeometry(0.045, 0.018, 0.05);
    rightShoulder.translate(0.09, 0.42, 0);
    parts.push(rightShoulder);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.RIGHT_SHOULDER);

    // ==================== LEFT ARM ====================
    // Left upper arm
    const leftUpperArm = new THREE.CylinderGeometry(0.025, 0.022, 0.08, 6);
    leftUpperArm.rotateZ(0.15);
    leftUpperArm.translate(-0.105, 0.38, 0);
    parts.push(leftUpperArm);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.LEFT_ARM);

    // Left forearm
    const leftForearm = new THREE.CylinderGeometry(0.022, 0.018, 0.07, 6);
    leftForearm.rotateZ(0.2);
    leftForearm.translate(-0.115, 0.30, 0);
    parts.push(leftForearm);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.LEFT_ARM);

    // Left hand
    const leftHand = new THREE.SphereGeometry(0.02, 6, 5);
    leftHand.translate(-0.125, 0.24, 0);
    parts.push(leftHand);
    colors.push(COLORS.skin);
    animGroups.push(SWORDSMAN_ANIM_GROUP.LEFT_ARM);

    // ==================== RIGHT ARM (SWORD ARM) ====================
    // Right upper arm
    const rightUpperArm = new THREE.CylinderGeometry(0.025, 0.022, 0.08, 6);
    rightUpperArm.rotateZ(-0.15);
    rightUpperArm.translate(0.105, 0.38, 0);
    parts.push(rightUpperArm);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.RIGHT_ARM);

    // Right forearm
    const rightForearm = new THREE.CylinderGeometry(0.022, 0.018, 0.07, 6);
    rightForearm.rotateZ(-0.2);
    rightForearm.translate(0.115, 0.30, 0);
    parts.push(rightForearm);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.RIGHT_ARM);

    // Right hand
    const rightHandX = 0.125;
    const rightHandY = 0.24;
    const rightHandZ = 0;
    const rightHand = new THREE.SphereGeometry(0.02, 6, 5);
    rightHand.translate(rightHandX, rightHandY, rightHandZ);
    parts.push(rightHand);
    colors.push(COLORS.skin);
    animGroups.push(SWORDSMAN_ANIM_GROUP.RIGHT_ARM);

    // ==================== LEGS ====================
    // Left thigh
    const leftThigh = new THREE.CylinderGeometry(0.032, 0.028, 0.08, 6);
    leftThigh.translate(-0.035, 0.12, 0);
    parts.push(leftThigh);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.LEFT_LEG);

    // Left shin
    const leftShin = new THREE.CylinderGeometry(0.028, 0.022, 0.07, 6);
    leftShin.translate(-0.035, 0.045, 0);
    parts.push(leftShin);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.LEFT_LEG);

    // Left foot
    const leftFoot = new THREE.BoxGeometry(0.035, 0.018, 0.05);
    leftFoot.translate(-0.035, 0.009, 0.008);
    parts.push(leftFoot);
    colors.push(COLORS.woodDark);
    animGroups.push(SWORDSMAN_ANIM_GROUP.LEFT_LEG);

    // Right thigh
    const rightThigh = new THREE.CylinderGeometry(0.032, 0.028, 0.08, 6);
    rightThigh.translate(0.035, 0.12, 0);
    parts.push(rightThigh);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.RIGHT_LEG);

    // Right shin
    const rightShin = new THREE.CylinderGeometry(0.028, 0.022, 0.07, 6);
    rightShin.translate(0.035, 0.045, 0);
    parts.push(rightShin);
    colors.push(COLORS.swordGreen);
    animGroups.push(SWORDSMAN_ANIM_GROUP.RIGHT_LEG);

    // Right foot
    const rightFoot = new THREE.BoxGeometry(0.035, 0.018, 0.05);
    rightFoot.translate(0.035, 0.009, 0.008);
    parts.push(rightFoot);
    colors.push(COLORS.woodDark);
    animGroups.push(SWORDSMAN_ANIM_GROUP.RIGHT_LEG);

    // ==================== SWORD (Chinese Dao style) ====================
    const swordTilt = -0.8; // Tilted to the right

    // Calculate grip offset
    const gripOffsetX = Math.sin(-swordTilt) * 0.04;
    const gripOffsetY = Math.cos(-swordTilt) * 0.04;

    // Sword handle
    const swordHandle = new THREE.CylinderGeometry(0.016, 0.014, 0.08, 6);
    swordHandle.rotateZ(swordTilt);
    swordHandle.translate(rightHandX + gripOffsetX * 0.3, rightHandY + gripOffsetY * 0.3, rightHandZ);
    parts.push(swordHandle);
    colors.push(COLORS.wood);
    animGroups.push(SWORDSMAN_ANIM_GROUP.SWORD);

    // Handle wrap
    const handleWrap = new THREE.TorusGeometry(0.018, 0.004, 4, 6);
    handleWrap.rotateY(Math.PI / 2);
    handleWrap.rotateZ(swordTilt);
    handleWrap.translate(rightHandX + gripOffsetX * 0.2, rightHandY + gripOffsetY * 0.2, rightHandZ);
    parts.push(handleWrap);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.SWORD);

    // Pommel ring (Chinese ring-pommel sword)
    const pommelRing = new THREE.TorusGeometry(0.02, 0.006, 6, 12);
    pommelRing.rotateY(Math.PI / 2);
    pommelRing.rotateZ(swordTilt);
    const pommelX = rightHandX - gripOffsetX * 0.5;
    const pommelY = rightHandY - gripOffsetY * 0.5;
    pommelRing.translate(pommelX, pommelY, rightHandZ);
    parts.push(pommelRing);
    colors.push(COLORS.gold);
    animGroups.push(SWORDSMAN_ANIM_GROUP.SWORD);

    // Guard
    const guard = new THREE.BoxGeometry(0.06, 0.018, 0.03);
    guard.rotateZ(swordTilt);
    const guardX = rightHandX + gripOffsetX * 1.2;
    const guardY = rightHandY + gripOffsetY * 1.2;
    guard.translate(guardX, guardY, rightHandZ);
    parts.push(guard);
    colors.push(COLORS.bronze);
    animGroups.push(SWORDSMAN_ANIM_GROUP.SWORD);

    // Blade
    const blade = new THREE.BoxGeometry(0.032, 0.26, 0.01);
    blade.rotateZ(swordTilt);
    const bladeX = rightHandX + gripOffsetX * 1.2 + Math.sin(-swordTilt) * 0.15;
    const bladeY = rightHandY + gripOffsetY * 1.2 + Math.cos(-swordTilt) * 0.15;
    blade.translate(bladeX, bladeY, rightHandZ);
    parts.push(blade);
    colors.push(COLORS.steel);
    animGroups.push(SWORDSMAN_ANIM_GROUP.SWORD);

    // Blade spine (thicker back)
    const bladeSpine = new THREE.BoxGeometry(0.012, 0.24, 0.015);
    bladeSpine.rotateZ(swordTilt);
    const spineOffsetX = Math.cos(swordTilt) * 0.012;
    const spineOffsetY = Math.sin(swordTilt) * 0.012;
    bladeSpine.translate(bladeX + spineOffsetX, bladeY - spineOffsetY, rightHandZ);
    parts.push(bladeSpine);
    colors.push(COLORS.iron);
    animGroups.push(SWORDSMAN_ANIM_GROUP.SWORD);

    // Blade tip
    const bladeTip = new THREE.ConeGeometry(0.016, 0.045, 4);
    bladeTip.rotateZ(swordTilt);
    const tipX = rightHandX + gripOffsetX * 1.2 + Math.sin(-swordTilt) * 0.30;
    const tipY = rightHandY + gripOffsetY * 1.2 + Math.cos(-swordTilt) * 0.30;
    bladeTip.translate(tipX, tipY, rightHandZ);
    parts.push(bladeTip);
    colors.push(COLORS.steel);
    animGroups.push(SWORDSMAN_ANIM_GROUP.SWORD);

    return this.mergeGeometriesWithAnimData(parts, colors, animGroups);
  }

  private mergeGeometriesWithAnimData(
    geometries: THREE.BufferGeometry[],
    colors: THREE.Color[],
    animGroups: number[]
  ): THREE.BufferGeometry {
    // Calculate total vertices
    let totalVertices = 0;
    for (const geo of geometries) {
      const nonIndexed = geo.index ? geo.toNonIndexed() : geo;
      totalVertices += nonIndexed.getAttribute('position').count;
    }

    // Create output arrays
    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);
    const vertexColors = new Float32Array(totalVertices * 3);
    const animGroupAttr = new Float32Array(totalVertices);

    let offset = 0;
    for (let i = 0; i < geometries.length; i++) {
      let geo = geometries[i];
      if (geo.index) {
        geo = geo.toNonIndexed();
      }

      const posAttr = geo.getAttribute('position');
      const normalAttr = geo.getAttribute('normal');
      const color = colors[i];
      const animGroup = animGroups[i];
      const count = posAttr.count;

      // Add subtle color variation
      const variation = 0.94 + Math.random() * 0.12;

      for (let j = 0; j < count; j++) {
        const idx = offset + j;

        // Position
        positions[idx * 3] = posAttr.getX(j);
        positions[idx * 3 + 1] = posAttr.getY(j);
        positions[idx * 3 + 2] = posAttr.getZ(j);

        // Normal
        if (normalAttr) {
          normals[idx * 3] = normalAttr.getX(j);
          normals[idx * 3 + 1] = normalAttr.getY(j);
          normals[idx * 3 + 2] = normalAttr.getZ(j);
        }

        // Color with variation
        vertexColors[idx * 3] = color.r * variation;
        vertexColors[idx * 3 + 1] = color.g * variation;
        vertexColors[idx * 3 + 2] = color.b * variation;

        // Animation group
        animGroupAttr[idx] = animGroup;
      }

      offset += count;
      geometries[i].dispose();
    }

    // Create merged geometry
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    merged.setAttribute('animGroup', new THREE.BufferAttribute(animGroupAttr, 1));
    merged.computeVertexNormals();

    return merged;
  }

  addSwordsman(entity: Entity, x: number, y: number, z: number, factionId: FactionId): void {
    if (this.entityMap.has(entity)) return;

    const data: SwordsmanData = {
      entity,
      x,
      y,
      z,
      factionId,
      rotation: 0,
      speed: 0,
      animPhase: Math.random() * Math.PI * 2,
      attackTime: -1,
      attackDuration: 0.55,
    };
    this.swordsmanData.push(data);
    this.entityMap.set(entity, data);
  }

  removeSwordsman(entity: Entity): void {
    const swordsman = this.entityMap.get(entity);
    if (!swordsman) return;

    const index = this.swordsmanData.indexOf(swordsman);
    if (index !== -1) {
      const last = this.swordsmanData[this.swordsmanData.length - 1];
      this.swordsmanData[index] = last;
      this.swordsmanData.pop();
    }
    this.entityMap.delete(entity);
  }

  updateSwordsman(entity: Entity, x: number, y: number, z: number, rotation?: number): void {
    const swordsman = this.entityMap.get(entity);
    if (!swordsman) return;

    // Calculate speed from movement
    const dx = x - swordsman.x;
    const dz = z - swordsman.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Smooth speed transition
    const targetSpeed = Math.min(distance * 30, 1.0);
    swordsman.speed = swordsman.speed * 0.85 + targetSpeed * 0.15;

    // Update rotation
    if (rotation !== undefined) {
      swordsman.rotation = rotation;
    } else if (distance > 0.001) {
      swordsman.rotation = Math.atan2(dx, dz);
    }

    swordsman.x = x;
    swordsman.y = y;
    swordsman.z = z;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update time uniform for all materials
    for (const material of this.materials.values()) {
      material.uniforms.uTime.value = this.time;
    }

    // Group swordsmen by faction for instanced rendering
    const factionGroups = new Map<FactionId, SwordsmanData[]>();
    for (const swordsman of this.swordsmanData) {
      if (!factionGroups.has(swordsman.factionId)) {
        factionGroups.set(swordsman.factionId, []);
      }
      factionGroups.get(swordsman.factionId)!.push(swordsman);
    }

    // Update each faction's instanced mesh
    for (const [factionId, mesh] of this.instancedMeshes) {
      const swordsmenList = factionGroups.get(factionId) || [];
      mesh.count = swordsmenList.length;

      if (swordsmenList.length === 0) continue;

      // Update instance attributes and matrices
      for (let i = 0; i < swordsmenList.length; i++) {
        const swordsman = swordsmenList[i];

        // Update instance attributes
        if (this.speedAttribute) {
          this.speedAttribute.setX(i, swordsman.speed);
        }
        if (this.phaseAttribute) {
          this.phaseAttribute.setX(i, swordsman.animPhase);
        }
        if (this.attackTimeAttribute) {
          this.attackTimeAttribute.setX(i, swordsman.attackTime);
        }

        // Update transform matrix
        const surfaceOffset = 0.02;
        this.dummy.position.set(swordsman.x, swordsman.y + surfaceOffset, swordsman.z);
        this.dummy.rotation.set(0, swordsman.rotation, 0);
        this.dummy.scale.setScalar(1.75);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(i, this.dummy.matrix);
      }

      // Mark for GPU update
      mesh.instanceMatrix.needsUpdate = true;
      if (this.speedAttribute) this.speedAttribute.needsUpdate = true;
      if (this.phaseAttribute) this.phaseAttribute.needsUpdate = true;
      if (this.attackTimeAttribute) this.attackTimeAttribute.needsUpdate = true;
    }
  }

  getSwordsmanCount(): number {
    return this.swordsmanData.length;
  }

  clear(): void {
    this.swordsmanData.length = 0;
    for (const mesh of this.instancedMeshes.values()) {
      mesh.count = 0;
    }
  }

  dispose(): void {
    // Remove event listeners
    if (this.attackEventHandler) {
      EventBus.off('unit:attack', this.attackEventHandler);
    }

    // Dispose meshes
    for (const mesh of this.instancedMeshes.values()) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
      this.scene.remove(mesh);
    }
    this.instancedMeshes.clear();
    this.materials.clear();
    this.swordsmanData.length = 0;
  }
}
